import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Spinner } from "@/components/Spinner";
import { useWelcomeGiftRegistrationMutation } from "@/hooks/useWelcomeGiftRegistrationMutation";
import type { WelcomeGiftRegistrationRequest } from "@da-web/common";
import { useWelcomeGiftStore } from "@/hooks/welcomeGiftStore";
import { useBrandConfigStore } from "@/hooks/brandConfigStore";
import { getSDK } from "@/lib/analytics";

export const Route = createFileRoute("/(welcome-gift)/welcome-gift/")({
  component: WelcomeGift,
});

function WelcomeGift() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<WelcomeGiftRegistrationRequest>({
    name: "",
    firstName: "",
    lastName: "",
    phone: "08",
    email: "",
    brandID: "",
  });

  const { config } = useBrandConfigStore()

  if (!config) return null;

  const isFormComplete = !!formData.email && !!formData.firstName && !!formData.lastName && !!formData.phone

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPayload } = useWelcomeGiftStore();

  const { mutate } = useWelcomeGiftRegistrationMutation();


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\D/g, "");
    setFormData({ ...formData, phone: value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true)
    e.preventDefault();
    setErrorMessage("");
    await getSDK().catch(() => null);
    const phoneNumber = formData.phone.startsWith("0") ? formData.phone.substring(1) : formData.phone
    const fullName = `${formData.firstName} ${formData.lastName}`
    const payload: WelcomeGiftRegistrationRequest = {
      ...formData,
      name: fullName,
      phone: `+62${phoneNumber}`,
      brandID: config.brandID,
    };
    mutate(payload, {
      onSuccess: async () => {
        localStorage.removeItem("otp_expiry")
        setPayload(payload)
        await navigate({ to: "/welcome-gift/otp", replace: true })
      },
      onError: (error: any) => {
        if (error?.error?.message) {
          setErrorMessage(`error: ${error.error.message}`);
        } else {
          setErrorMessage(`error: ${error.message}`);
        }
        setIsLoading(false)
      },
    });
  };

  const isButtonDisabled = isLoading || !isFormComplete


  return (
    <div className="flex flex-col w-full h-full justify-center" style={{ fontFamily: config.fonts.mainFont }}>
      <div
        className="flex flex-col items-center text-white pb-12 h-[95%] sm:h-[80%] px-6"
        style={{ backgroundColor: config.bgColors.mainColor }}
      >
        <img src={config.logoUrl} alt="Logo" style={config.logoStyle} />
        <p className="text-3xl font-bold text-center" style={{ fontFamily: config.fonts.titleFont }}>
          A SMALL TREAT TO
        </p>
        <p className="text-3xl font-bold text-center" style={{ fontFamily: config.fonts.titleFont }}>
          SAY WELCOME
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 pt-10">
          <div className="flex">
            <input
              placeholder="FIRST NAME"
              type="text"
              name="firstName"
              id="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="block w-full focus:outline-none px-3 py-2 mt-1 text-gray-900 bg-gray-100 border border-gray-300 rounded-md text-sm sm:text-sm mr-2"
              required
            />
            <input
              placeholder="LAST NAME"
              type="text"
              name="lastName"
              id="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="block w-full focus:outline-none px-3 py-2 mt-1 text-gray-900 bg-gray-100 border border-gray-300 rounded-md text-sm sm:text-sm"
              required
            />
          </div>
          <div>
            <div className="flex items-center w-full px-3 py-2 mt-1 text-gray-900 bg-gray-100 border border-gray-300 rounded-md">
              <input
                type="tel"
                minLength={9}
                maxLength={15}
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="w-full focus:outline-none bg-transparent border-none text-sm sm:text-sm"
                placeholder="0822xxx"
                required
              />
            </div>
          </div>
          <div>
            <input
              placeholder="EMAIL"
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="block w-full focus:outline-none px-3 py-2 mt-1 text-gray-900 bg-gray-100 border border-gray-300 rounded-md text-sm sm:text-sm"
              required
            />
          </div>
          <div className="flex justify-center">
            <button
              type="submit"
              className={`w-[50%] px-4 py-2 font-bold rounded-md flex justify-center items-center`}
              style={{
                backgroundColor: isButtonDisabled ? "#9ca3af" : config.buttonColors.bgColor,
                color: isButtonDisabled ? "#ffffff" : config.buttonColors.fontColor
              }}
              disabled={isButtonDisabled}
            >
              {isLoading ? (
                <Spinner className="w-5 h-5 text-sm text-white" />
              ) : (
                "SUBMIT"
              )}
            </button>
          </div>
          {errorMessage && <p className="text-white-600 text-center">{errorMessage}</p>}
        </form>
        <div className="mt-auto">
          <p className="text-xl font-bold text-center">*for our first time customer</p>
        </div>
      </div>
    </div>
  );
}
